import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '../Icons';

interface FilterPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
  align?: 'left' | 'right' | 'center';
}

export const FilterPopover: React.FC<FilterPopoverProps> = ({ 
  isOpen, 
  onClose, 
  children, 
  title,
  align = 'left' 
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const mobileModalRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.setAttribute('data-filter-modal-open', 'true');
    } else {
      document.body.removeAttribute('data-filter-modal-open');
    }
    return () => {
      document.body.removeAttribute('data-filter-modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Do not close if clicking inside desktop popover
      if (popoverRef.current && popoverRef.current.contains(target)) {
        return;
      }
      // Do not close if clicking inside mobile modal
      if (mobileModalRef.current && mobileModalRef.current.contains(target)) {
        return;
      }
      if ((event.target as Element)?.closest?.('[data-filter-modal-content]')) {
        return;
      }
      onClose();
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const alignClasses = {
    left: 'left-0 translate-x-0',
    right: 'right-0 left-auto translate-x-0',
    center: 'left-1/2 -translate-x-1/2'
  };

  const arrowClasses = {
    left: 'left-6',
    right: 'right-6',
    center: 'left-1/2 -translate-x-1/2'
  };

  return (
    <>
      {/* Mobile Modal via React Portal to document.body (completely above everything, z-[100]) */}
      {mounted && createPortal(
        <div className="md:hidden">
          {/* Mobile Dark Backdrop covering full screen */}
          <div 
            className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm animate-in fade-in duration-200" 
            onClick={onClose} 
          />
          
          {/* Mobile Centered Modal */}
          <div 
            ref={mobileModalRef}
            data-filter-modal-content="true"
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-[92%] max-w-sm max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 flex flex-col animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100 flex-shrink-0">
              <h4 className="text-xs font-black text-sky-900 uppercase tracking-widest">{title}</h4>
              <button 
                type="button" 
                onClick={onClose} 
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                aria-label="Cerrar filtro"
              >
                <XMarkIcon className="w-5 h-5"/>
              </button>
            </div>
            
            <div className="custom-scrollbar overflow-y-auto overflow-x-hidden flex-grow">
              {children}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Desktop Anchored Popover (remains anchored right below the filter button) */}
      <div 
        ref={popoverRef} 
        className={`
          hidden md:flex flex-col absolute top-full translate-y-0 mt-3 w-80 max-w-[calc(100vw-2rem)] max-h-[calc(100vh-140px)] bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 z-[70] animate-fade-in-up
          ${alignClasses[align]}
        `}
      >
        <div className={`absolute -top-2 w-4 h-4 bg-white border-t border-l border-gray-100 rotate-45 ${arrowClasses[align]}`}></div>
        
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-50 flex-shrink-0">
          <h4 className="text-xs font-black text-sky-900 uppercase tracking-widest">{title}</h4>
          <button 
            type="button" 
            onClick={onClose} 
            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
            aria-label="Cerrar filtro"
          >
            <XMarkIcon className="w-5 h-5"/>
          </button>
        </div>
        
        <div className="custom-scrollbar overflow-y-auto overflow-x-hidden flex-grow">
          {children}
        </div>
      </div>
    </>
  );
};
